import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { apiFetch } from '../api/client';
import type { ResetResult, SyncStatus } from '../api/types';

export const ResetButton = () => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  const { data: sync } = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => apiFetch<SyncStatus>('/api/sync'),
  });

  const reset = useMutation({
    mutationFn: () => apiFetch<ResetResult>('/api/sync', { method: 'DELETE' }),
    onSuccess: (res) => {
      setMessage({
        type: 'ok',
        text: res.status === 'reset' ? '差分を破棄しました' : '差分はありませんでした',
      });
      void queryClient.invalidateQueries();
    },
    onError: (e: Error) => {
      setMessage({ type: 'error', text: e.message });
    },
  });

  // 差分がなければボタン自体を出さない。バナーの「処理中」と被らないよう
  // open PR がある場合は backend で 409 を返す (= ボタンは押せるがガード) 形に統一。
  if (sync === undefined || sync.branchAhead === 0) return null;

  const hasOpenPr = sync.latestPullRequest !== null && sync.latestPullRequest.state === 'open';

  const onClick = () => {
    if (
      !window.confirm(
        `翻訳ブランチ (${sync.branch}) の未公開の変更 ${sync.branchAhead.toString()} コミットを破棄して main の最新状態に戻します。元に戻せません。本当に破棄しますか?`,
      )
    ) {
      return;
    }
    setMessage(null);
    reset.mutate();
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        disabled={reset.isPending || hasOpenPr}
        title={
          hasOpenPr
            ? '公開処理中の PR があるため破棄できません。PR の完了を待つか手動で閉じてください'
            : '翻訳ブランチを main の最新状態に巻き戻して未公開の変更を全破棄します'
        }
        className="inline-flex h-8 items-center rounded border border-red-300 px-3 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {reset.isPending ? '破棄中...' : '差分を破棄'}
      </button>
      {message !== null && (
        <div className="absolute top-full right-0 z-40 mt-1 w-72 rounded border bg-white p-2 shadow-lg">
          <div className="flex items-start gap-2">
            <p
              className={`flex-1 text-xs ${
                message.type === 'ok' ? 'text-green-700' : 'text-red-700'
              }`}
            >
              {message.text}
            </p>
            <button
              type="button"
              onClick={() => setMessage(null)}
              className="text-gray-400 hover:text-gray-700"
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
